/**
 * useEhrRegistry
 *
 * Fetches the active EHR systems from the data-driven registry
 * (GET /api/v1/ehr/registry) and exposes them as a slug->label map, an options
 * list, and a label resolver. Replaces the hard-coded EHR lists previously
 * duplicated across the dashboard, org modal, and encounter views.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { apiService } from '../services/api';

export interface EhrRegistryEntry {
  slug: string;
  displayName: string;
  capabilities?: Record<string, unknown>;
  isActive: boolean;
  defaultWebhookId?: string | null;
  /** Console callers only: the endpoint omits these for a role that cannot read the webhook list. */
  defaultWebhookName?: string | null;
  defaultWebhookActive?: boolean | null;
  connectorStatus: 'ready' | 'none';
}

export function useEhrRegistry() {
  const [systems, setSystems] = useState<EhrRegistryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRegistry = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiService.get<{ success: boolean; data: EhrRegistryEntry[] }>(
        '/v1/ehr/registry'
      );
      setSystems(res?.data ?? []);
      setError(null);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load EHR registry');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRegistry();
  }, [fetchRegistry]);

  const labels = useMemo(
    () => systems.reduce<Record<string, string>>((acc, s) => {
      acc[s.slug] = s.displayName;
      return acc;
    }, {}),
    [systems]
  );

  const options = useMemo(
    () => systems.map(s => ({ value: s.slug, label: s.displayName })),
    [systems]
  );

  const labelFor = useCallback((slug: string) => labels[slug] ?? slug, [labels]);

  return { systems, labels, options, labelFor, loading, error, refetch: fetchRegistry };
}
